import { ProjectStatus } from './dto/projectShare';

/**
 * Who somebody is on one project, answered from the project and nothing else.
 *
 * An invited estimator is the firm doing the job: the server names them the project's provider
 * Owner when the client submits. Every provider control used to ask `rights` instead, and rights
 * come from the company roster, which somebody on the account only for this project is refused -
 * so the firm that was sent the work looked like nobody at all and could not accept it, price it
 * or staff it.
 *
 * Asking the project is both narrower and the truth. It says nothing about the company, so it
 * unlocks nothing company-wide: the marketplace, the AI Hub, billing and inviting new firm
 * teammates stay where they are, behind claiming the account. Estimating an assigned project and
 * buying a plan are different decisions, usually made by different people.
 */

/** The little of a project this file needs, so it works for any project shape. */
type PartyLike = {
  id?: string;
  isProvider?: boolean | null;
};

export type ProjectLike = {
  id?: string;
  status?: ProjectStatus;
  resourceUsers?: PartyLike[] | null;
  users?: PartyLike[] | null;
};

/**
 * The statuses that mean the provider has taken the work on.
 *
 * Draft, New and Submitted are before the provider has answered. Rejected and Canceled are after
 * the engagement ended, with nothing in flight, so neither counts as having accepted either. Kept
 * in step with ProjectParty.AcceptedOrLater on the server.
 */
const ACCEPTED_OR_LATER: ProjectStatus[] = [
  ProjectStatus.Accepted,
  ProjectStatus.FeeSelected,
  ProjectStatus.FeeReady,
  ProjectStatus.FeeCounter,
  ProjectStatus.Approved,
  ProjectStatus.InProgress,
  ProjectStatus.Complete,
  ProjectStatus.SummaryReady,
];

const listed = (people: PartyLike[] | null | undefined, userId: string): boolean =>
  people?.some((u) => u.id === userId && u.isProvider === true) === true;

/**
 * Whether this person is the provider on this project.
 *
 * Both collections are read because they are the same fact written twice: `resourceUsers` is the
 * project's own record and `users` is it hydrated. Either answering is enough, which also means
 * this keeps working while a release has reached one side and not the other.
 * @param project - The project.
 * @param userId - The person.
 * @returns True when the project records them as its provider.
 */
export const isProjectProvider = (project?: ProjectLike | null, userId?: string | null): boolean => {
  if (!project || !userId) return false;
  return listed(project.resourceUsers, userId) || listed(project.users, userId);
};

/** Whether the provider has taken this project on, rather than merely being named on it. */
export const projectIsAccepted = (project?: ProjectLike | null): boolean =>
  !!project?.status && ACCEPTED_OR_LATER.includes(project.status);

/**
 * Whether this person is the provider on this project AND has taken the work on.
 *
 * Being named as the provider happens at submit, before anybody has agreed to anything. Accepting
 * is the separate act that makes them a party to the engagement, and it is the line "either party
 * may withdraw" draws.
 * @param project - The project.
 * @param userId - The person.
 * @returns True when they are the provider and the project is past Submitted.
 */
export const isAcceptedProjectProvider = (project?: ProjectLike | null, userId?: string | null): boolean =>
  isProjectProvider(project, userId) && projectIsAccepted(project);

/**
 * Whether to offer this person the provider's side of this project.
 *
 * Either route counts, and the existing one is first: a firm that owns its account works exactly
 * as it did, and the project relationship is the addition rather than the replacement.
 * @param input - What is known about the viewer.
 * @returns True for the project's provider, by either route.
 */
export const actsAsProjectProvider = (input: {
  /** `rights.userType === UserType.provider` - the claimed firm's answer. */
  rightsSayProvider?: boolean;
  /** `isProjectProvider(project, me.id)` - the project's own answer. */
  projectProvider?: boolean;
}): boolean => input.rightsSayProvider === true || input.projectProvider === true;

/**
 * Whether to show this person the fee for this project.
 *
 * `rights.canViewFees` needs an Owner row on the company, which an invited estimator does not
 * have and does not need: developing the fee is the job they were sent. It stays sufficient, so
 * nothing changes for a claimed firm or for the client's fee decider.
 * @param input - What is known about the viewer.
 * @returns True when the fee belongs in front of them.
 */
export const canSeeProjectFees = (input: {
  /** `rights.canViewFees`. */
  rightsSayFees?: boolean;
  /** `isProjectProvider(project, me.id)`. */
  projectProvider?: boolean;
}): boolean => input.rightsSayFees === true || input.projectProvider === true;
